import React, { useState, useEffect } from 'react';
import { Album, ShelfConfig } from '../../types/album';
import { theme } from '../../styles/theme';
import { 
  calculateShelfBoundaries,
  validateShelfConfiguration 
} from '../../utils/shelfCalculation';

interface ShelfSettingsProps {
  albums: Album[];
  config: ShelfConfig;
  onConfigChange: (config: ShelfConfig) => void;
  onClose: () => void;
}

const ShelfSettings: React.FC<ShelfSettingsProps> = ({ 
  albums, 
  config, 
  onConfigChange, 
  onClose 
}) => {
  const [rows, setRows] = useState(config.rows);
  const [columns, setColumns] = useState(config.columns);
  const [activeShelves, setActiveShelves] = useState(config.activeShelves);
  const [heightRatio, setHeightRatio] = useState(config.shelfDimensions.heightRatio);
  const [widthRatio, setWidthRatio] = useState(config.shelfDimensions.widthRatio);
  
  const [shelfDefinitions, setShelfDefinitions] = useState<Array<{
    shelfNumber: number;
    firstAlbumId: string;
    lastAlbumId: string;
  }>>([]);
  
  const [errors, setErrors] = useState<string[]>([]);
  const sortedAlbums = albums.sort((a, b) => a.sortKey.localeCompare(b.sortKey));

  useEffect(() => {
    // Initialize with existing config if available
    if (config.shelfBoundaries.length > 0) {
      const definitions = config.shelfBoundaries.map(boundary => ({
        shelfNumber: boundary.shelfNumber,
        firstAlbumId: boundary.firstAlbumId,
        lastAlbumId: boundary.lastAlbumId,
      }));
      setShelfDefinitions(definitions);
    } else {
      // Initialize empty definitions for active shelves
      const emptyDefinitions = Array.from({ length: activeShelves }, (_, i) => ({
        shelfNumber: i + 1,
        firstAlbumId: '',
        lastAlbumId: '',
      }));
      setShelfDefinitions(emptyDefinitions);
    }
  }, [config, activeShelves]);

  // Update shelf definitions when activeShelves changes
  useEffect(() => {
    setShelfDefinitions(prev => {
      const newDefs = Array.from({ length: activeShelves }, (_, i) => {
        const existing = prev.find(def => def.shelfNumber === i + 1);
        return existing || {
          shelfNumber: i + 1,
          firstAlbumId: '',
          lastAlbumId: '',
        };
      });
      return newDefs;
    });
  }, [activeShelves]);

  const handleShelfDefinitionChange = (
    shelfNumber: number, 
    field: 'firstAlbumId' | 'lastAlbumId', 
    value: string
  ) => {
    setShelfDefinitions(prev => 
      prev.map(def => 
        def.shelfNumber === shelfNumber 
          ? { ...def, [field]: value }
          : def
      )
    );
  };

  const handleSave = () => {
    const validDefinitions = shelfDefinitions.filter(def => 
      def.firstAlbumId && def.lastAlbumId
    );
    
    const boundaries = calculateShelfBoundaries(validDefinitions, albums);
    const newConfig: ShelfConfig = {
      rows,
      columns,
      activeShelves,
      shelfDimensions: {
        heightRatio,
        widthRatio,
      },
      shelfBoundaries: boundaries,
    };
    
    const validation = validateShelfConfiguration(newConfig, albums);
    
    if (validation.isValid) {
      onConfigChange(newConfig);
      onClose();
    } else {
      setErrors(validation.errors);
    }
  };

  const handlePreset = (preset: 'kallax3x4' | 'kallax2x4' | 'kallax4x4' | 'custom') => {
    switch (preset) {
      case 'kallax3x4':
        setRows(4);
        setColumns(3);
        setActiveShelves(8);
        setHeightRatio(1.0);
        setWidthRatio(1.0);
        break;
      case 'kallax2x4':
        setRows(4);
        setColumns(2);
        setActiveShelves(8);
        setHeightRatio(1.0);
        setWidthRatio(1.0);
        break;
      case 'kallax4x4':
        setRows(4);
        setColumns(4);
        setActiveShelves(16);
        setHeightRatio(1.0);
        setWidthRatio(1.0);
        break;
    }
  };

  const maxShelves = rows * columns;

  const overlayStyle: React.CSSProperties = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: theme.zIndex.modal,
  };

  const modalStyle: React.CSSProperties = {
    backgroundColor: theme.colors.background,
    borderRadius: theme.borderRadius.lg,
    padding: theme.spacing.xl,
    maxWidth: '800px',
    width: '90%',
    maxHeight: '90vh',
    overflow: 'auto',
    position: 'relative',
  };

  const titleStyle: React.CSSProperties = {
    fontSize: '1.5rem',
    fontWeight: '700',
    color: theme.colors.text,
    marginBottom: theme.spacing.lg,
    textAlign: 'center',
  };

  const sectionStyle: React.CSSProperties = {
    marginBottom: theme.spacing.lg,
    padding: theme.spacing.md,
    backgroundColor: theme.colors.surface,
    borderRadius: theme.borderRadius.md,
  };

  const sectionTitleStyle: React.CSSProperties = {
    fontSize: '1.125rem',
    fontWeight: '600',
    color: theme.colors.text,
    marginBottom: theme.spacing.md,
  };

  const inputGroupStyle: React.CSSProperties = {
    display: 'flex',
    gap: theme.spacing.md,
    alignItems: 'center',
    marginBottom: theme.spacing.sm,
    flexWrap: 'wrap',
  };

  const labelStyle: React.CSSProperties = {
    fontSize: '0.875rem',
    color: theme.colors.textSecondary,
    minWidth: '120px',
  };

  const inputStyle: React.CSSProperties = {
    padding: theme.spacing.sm,
    borderRadius: theme.borderRadius.sm,
    border: `1px solid ${theme.colors.border}`,
    backgroundColor: theme.colors.background,
    color: theme.colors.text,
    fontSize: '0.875rem',
    width: '100px',
  };

  const selectStyle: React.CSSProperties = {
    ...inputStyle,
    width: '200px',
  };

  const presetButtonStyle: React.CSSProperties = {
    padding: `${theme.spacing.xs} ${theme.spacing.sm}`,
    marginRight: theme.spacing.sm,
    marginBottom: theme.spacing.xs,
    backgroundColor: theme.colors.surfaceLight,
    border: `1px solid ${theme.colors.border}`,
    borderRadius: theme.borderRadius.sm,
    color: theme.colors.text,
    fontSize: '0.75rem',
    cursor: 'pointer',
  };

  const buttonStyle: React.CSSProperties = {
    backgroundColor: theme.colors.primary,
    color: 'white',
    border: 'none',
    borderRadius: theme.borderRadius.md,
    padding: `${theme.spacing.sm} ${theme.spacing.md}`,
    fontSize: '0.875rem',
    fontWeight: '500',
    cursor: 'pointer',
    transition: `all ${theme.transitions.fast}`,
    marginRight: theme.spacing.sm,
  };

  const closeButtonStyle: React.CSSProperties = {
    position: 'absolute',
    top: theme.spacing.md,
    right: theme.spacing.md,
    background: 'none',
    border: 'none',
    fontSize: '1.5rem',
    color: theme.colors.textMuted,
    cursor: 'pointer',
  };

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
        <button style={closeButtonStyle} onClick={onClose}>×</button>
        
        <div style={titleStyle}>Shelf Configuration</div>

        {/* Preset Configurations */}
        <div style={sectionStyle}>
          <div style={sectionTitleStyle}>Quick Presets</div>
          <div>
            <button style={presetButtonStyle} onClick={() => handlePreset('kallax3x4')}>
              Kallax 3×4 (12 slots, 8 active)
            </button>
            <button style={presetButtonStyle} onClick={() => handlePreset('kallax2x4')}>
              Kallax 2×4 (8 slots, all active)
            </button>
            <button style={presetButtonStyle} onClick={() => handlePreset('kallax4x4')}>
              Kallax 4×4 (16 slots, all active)
            </button>
          </div>
        </div>

        {/* Grid Configuration */}
        <div style={sectionStyle}>
          <div style={sectionTitleStyle}>Grid Layout</div>
          
          <div style={inputGroupStyle}>
            <label style={labelStyle}>Rows (vertical):</label>
            <input
              type="number"
              min="1"
              max="10"
              value={rows}
              onChange={(e) => setRows(parseInt(e.target.value) || 1)}
              style={inputStyle}
            />
          </div>
          
          <div style={inputGroupStyle}>
            <label style={labelStyle}>Columns (horizontal):</label>
            <input
              type="number"
              min="1"
              max="10"
              value={columns}
              onChange={(e) => setColumns(parseInt(e.target.value) || 1)}
              style={inputStyle}
            />
          </div>
          
          <div style={inputGroupStyle}>
            <label style={labelStyle}>Active shelves:</label>
            <input
              type="number"
              min="1"
              max={maxShelves}
              value={activeShelves}
              onChange={(e) => setActiveShelves(Math.min(parseInt(e.target.value) || 1, maxShelves))}
              style={inputStyle}
            />
            <span style={{ fontSize: '0.75rem', color: theme.colors.textMuted }}>
              (max {maxShelves} for {rows}×{columns} grid)
            </span>
          </div>
        </div>

        {/* Shelf Dimensions */}
        <div style={sectionStyle}>
          <div style={sectionTitleStyle}>Shelf Dimensions</div>
          
          <div style={inputGroupStyle}>
            <label style={labelStyle}>Height ratio:</label>
            <input
              type="number"
              min="0.1"
              max="5"
              step="0.1"
              value={heightRatio}
              onChange={(e) => setHeightRatio(parseFloat(e.target.value) || 1.0)}
              style={inputStyle}
            />
            <span style={{ fontSize: '0.75rem', color: theme.colors.textMuted }}>
              (1.0 = square, 0.5 = short, 2.0 = tall)
            </span>
          </div>
          
          <div style={inputGroupStyle}>
            <label style={labelStyle}>Width ratio:</label>
            <input
              type="number"
              min="0.1"
              max="5"
              step="0.1"
              value={widthRatio}
              onChange={(e) => setWidthRatio(parseFloat(e.target.value) || 1.0)}
              style={inputStyle}
            />
            <span style={{ fontSize: '0.75rem', color: theme.colors.textMuted }}>
              (1.0 = square, 2.0 = wide, 0.5 = narrow)
            </span>
          </div>
        </div>

        {/* Shelf Boundaries */}
        <div style={sectionStyle}>
          <div style={sectionTitleStyle}>Shelf Boundaries</div>
          <div style={{ fontSize: '0.875rem', color: theme.colors.textSecondary, marginBottom: theme.spacing.md }}>
            Define the first and last album for each shelf (albums sorted alphabetically by artist):
          </div>
          
          {shelfDefinitions.map((def) => (
            <div key={def.shelfNumber} style={{ 
              display: 'flex', 
              gap: theme.spacing.md, 
              alignItems: 'center', 
              marginBottom: theme.spacing.sm,
              flexWrap: 'wrap',
            }}>
              <div style={{ minWidth: '80px', fontSize: '0.875rem', fontWeight: '500' }}>
                Shelf {def.shelfNumber}:
              </div>
              
              <select
                value={def.firstAlbumId}
                onChange={(e) => handleShelfDefinitionChange(def.shelfNumber, 'firstAlbumId', e.target.value)}
                style={selectStyle}
              >
                <option value="">Select first album...</option>
                {sortedAlbums.map(album => (
                  <option key={album.id} value={album.id}>
                    {album.artist} - {album.title}
                  </option>
                ))}
              </select>
              
              <span style={{ fontSize: '0.875rem', color: theme.colors.textMuted }}>to</span>
              
              <select
                value={def.lastAlbumId}
                onChange={(e) => handleShelfDefinitionChange(def.shelfNumber, 'lastAlbumId', e.target.value)}
                style={selectStyle}
              >
                <option value="">Select last album...</option>
                {sortedAlbums.map(album => (
                  <option key={album.id} value={album.id}>
                    {album.artist} - {album.title}
                  </option>
                ))}
              </select>
            </div>
          ))}
        </div>

        {/* Errors */}
        {errors.length > 0 && (
          <div style={{
            backgroundColor: `${theme.colors.error}20`,
            borderRadius: theme.borderRadius.md,
            padding: theme.spacing.md,
            marginBottom: theme.spacing.lg,
          }}>
            <div style={{ color: theme.colors.error, fontWeight: '600', marginBottom: theme.spacing.sm }}>
              Configuration Errors:
            </div>
            {errors.map((error, index) => (
              <div key={index} style={{ color: theme.colors.error, fontSize: '0.875rem' }}>
                • {error}
              </div>
            ))}
          </div>
        )}

        {/* Action Buttons */}
        <div style={{ display: 'flex', justifyContent: 'center', gap: theme.spacing.md }}>
          <button style={buttonStyle} onClick={handleSave}>
            Save Configuration
          </button>
          <button 
            style={{
              ...buttonStyle,
              backgroundColor: theme.colors.surface,
              color: theme.colors.text,
            }}
            onClick={onClose}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default ShelfSettings;